import { useEffect, useState } from 'react';

function getMediaQueryMatch(query: string) {
  if (
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function'
  ) {
    return null;
  }

  return window.matchMedia(query).matches;
}

export interface ResponsiveValueRule<T> {
  query: string;
  value: T;
}

function resolveResponsiveValue<T>(
  rules: ResponsiveValueRule<T>[],
  defaultValue: T,
) {
  let resolvedValue = defaultValue;

  for (const rule of rules) {
    if (getMediaQueryMatch(rule.query)) {
      resolvedValue = rule.value;
    }
  }

  return resolvedValue;
}

export function useResponsiveValue<T>(
  rules: ResponsiveValueRule<T>[],
  defaultValue: T,
  enabled = true,
) {
  const [value, setValue] = useState<T>(() =>
    enabled ? resolveResponsiveValue(rules, defaultValue) : defaultValue,
  );

  useEffect(() => {
    if (!enabled) {
      setValue(defaultValue);
      return;
    }

    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      setValue(defaultValue);
      return;
    }

    const mediaQueryLists = rules.map((rule) => window.matchMedia(rule.query));

    const syncValue = () => {
      setValue(resolveResponsiveValue(rules, defaultValue));
    };

    syncValue();

    mediaQueryLists.forEach((mediaQueryList) => {
      mediaQueryList.addEventListener('change', syncValue);
    });
    window.addEventListener('resize', syncValue);

    return () => {
      mediaQueryLists.forEach((mediaQueryList) => {
        mediaQueryList.removeEventListener('change', syncValue);
      });
      window.removeEventListener('resize', syncValue);
    };
  }, [defaultValue, enabled, rules]);

  return value;
}

export function useMediaQuery(query: string, enabled = true) {
  const responsiveValue = useResponsiveValue(
    [{ query, value: true }],
    false,
    enabled,
  );

  const [matches, setMatches] = useState<boolean | null>(() =>
    enabled ? getMediaQueryMatch(query) : null,
  );

  useEffect(() => {
    if (!enabled) {
      setMatches(null);
      return;
    }

    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      setMatches(null);
      return;
    }

    setMatches(responsiveValue);
  }, [enabled, query, responsiveValue]);

  return matches;
}
